<template>
  <v-layout column>
    <list-edition-toolbar
      :buttons="toolbarButtons"
      @edit="editionDisabled = false"
      @delete="deleteDialog = true"
      @save="save"
      @cancel="cancel"
    />

    <v-layout row wrap class="activity-form-container" v-if="!loading">
      <input-field
        :label="$t('Title')"
        v-model="editedActivity.title"
        variable-name="title"
        input-type="text"
        size="small"
        :field-disabled="editionDisabled"
        required
      />

      <input-field
        :label="$t('DueDate')"
        v-model="editedActivity.due_date"
        variable-name="due_date"
        input-type="datetime"
        size="small"
        :field-disabled="editionDisabled || activity.state === 'closed'"
        :required="activity.due_date !== null"
      />

      <input-field
        :label="$t('State')"
        :value="activity.state"
        variable-name="state"
        input-type="other"
        size="small"
        :field-disabled="true"
      >
        <v-layout row align-center>
          <v-flex shrink>
            <activity-task-status-icon :status="activity.state" />
          </v-flex>

          <v-flex class="ml-2">
            <div>{{ getStateLabel(activity.state) }}</div>
          </v-flex>
        </v-layout>
      </input-field>

      <input-field
        :label="$t('Description')"
        v-model="editedActivity.description"
        variable-name="description"
        input-type="textarea"
        size="medium"
        :field-disabled="editionDisabled"
      />
      <input-field
        :label="$t('ApprovalType')"
        input-type="other"
        size="small"
        :value="approvalType"
      >
        <treeselect
          :value="approvalType"
          :options="approvalTypeOptions"
          @input="updateTasksAutoApproval($event)"
          class="form-input-select"
          :disabled="editionDisabled"
        />
      </input-field>

      <!--<input-field
        :label="$tc('Dependency', 2)"
        :value="editedActivity.dependencies"
        variable-name="dependencies"
        input-type="other"
        size="large"
        :field-disabled="editionDisabled"
      >
        <treeselect
          v-model="editedActivity.dependencies"
          :options="dependencyOptions"
          :load-options="loadDependencyOptions"
          :normalizer="normalizer"
          multiple
          :disabled="editionDisabled"
          disableFuzzyMatching
          class="form-input-select"
        />
      </input-field>-->
    </v-layout>
    <div v-if="loading" style="position:relative; width: 100%; height: 100px">
      <loading-spinner
        :active.sync="loading"
        :is-full-page="false"
        color="#4fa2b7"
        background-color="transparent"
      />
    </div>

    <app-dialog-box
      :open="deleteDialog"
      :title="$t('DeleteItem')"
      :content="$t('DeleteConfirmation') + ' ' + activity.title + '?'"
      :main-action-label="$t('No')"
      @mainAction="deleteDialog = false"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="remove"
    />
  </v-layout>
</template>

<script>
import { mapActions } from "vuex";
import InputField from "@/components/Home/Forms/_Molecules/InputField";
import ListEditionToolbar from "@/components/Home/Forms/_Molecules/ListEditionToolbar";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import ActivityTaskStatusIcon from "../../../../../FieldManagement/StatusIcon";
import _ from "lodash";
import { activitiesTasksDataMixin } from "@/mixins/field/activitiesTasksDataMixin";
import LoadingSpinner from "vue-loading-overlay";

export default {
  name: "ObjectFormActivityDetailsForm",

  mixins: [activitiesTasksDataMixin],

  components: {
    ActivityTaskStatusIcon,
    AppDialogBox,
    ListEditionToolbar,
    InputField,
    LoadingSpinner
  },

  props: {
    /**
     * received existing activity
     */
    activity: {
      type: Object,
      required: true
    },
    /**
     * received existing tasks
     */
    tasks: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      editionDisabled: true,

      editedActivity: {},
      /**
       * copy of received tasks to change approval_type
       */
      tasksToEdit: [],
      dependencyOptions: null,

      deleteDialog: false,
      loading: false
    };
  },

  computed: {
    /**
     * if when editing if every item is true returns the corresponding selected title
     */
    approvalType() {
      let found = "automatic";
      if (this.tasks.every(item => item.auto_approval === true)) {
        found = true;
      } else if (!this.tasks.every(item => item.auto_approval === true)) {
        found = false;
      }
      return found;
    },
    /**
     * list of approval types
     */
    approvalTypeOptions() {
      return [
        {
          label: this.$i18n.t("Automatic"),
          id: "automatic",
          isDisabled: true
        },
        {
          label: this.$i18n.t("AutoApproved"),
          id: true
        },
        {
          label: this.$i18n.t("ManualApproval"),
          id: false
        }
      ];
    },
    toolbarButtons() {
      return [
        {
          id: "edit",
          icon: "edit",
          tooltip: this.$i18n.t("Edit"),
          active: this.editionDisabled,
          disabled: this.actionType === "view"
        },
        {
          id: "cancel",
          icon: "cancel",
          tooltip: this.$i18n.t("Cancel"),
          active: !this.editionDisabled,
          disabled: false
        },
        {
          id: "save",
          icon: "save",
          tooltip: this.$i18n.t("Save"),
          active: !this.editionDisabled,
          disabled: false
        },
        {
          id: "delete",
          icon: "delete",
          tooltip: this.$i18n.t("DeleteActivity"),
          active: this.editionDisabled
        }
      ];
    }
  },

  mounted() {
    this.setFormData();
  },

  methods: {
    ...mapActions(["updateActivity", "deleteActivity", "updateTask"]),

    loadDependencyOptions({ callback }) {
      //TODO Implement loadDependencyOptions final version
      callback();
    },

    normalizer(node) {
      return {
        id: node.id,
        label: node.id + ": " + node.title
      };
    },
    /**
     * creates copies of received activity and tasks
     */
    setFormData() {
      this.editedActivity = Object.assign({}, this.activity);
      this.tasksToEdit = _.cloneDeep(this.tasks);
    },

    stopEditing() {
      this.editionDisabled = true;
    },

    cancel() {
      this.setFormData();
      this.stopEditing();
    },

    async save() {
      const updatedData = _.omitBy(
        this.editedActivity,
        (value, key) => this.activity[key] === value
      );
      try {
        this.loading = true;
        if (Object.keys(updatedData).length > 0) {
          await this.updateActivity({
            activityUUID: this.activity.uuid,
            updatedData: updatedData
          });
        }

        this.stopEditing();
        this.$emit("update");
      } catch (e) {
        console.error(e);
        this.$toasted.global.toast_error(e.response.data.message);
      } finally {
        this.loading = false;
      }
    },

    async remove() {
      try {
        this.loading = true;
        await this.deleteActivity(this.activity.uuid);
        this.$emit("delete");
      } catch (e) {
        console.error(e);
        this.$toasted.global.toast_error(e.response.data.message);
        this.loading = false;
      }
    },

    /**
     * updates tasks auto approval value
     */
    updateTasksAutoApproval(value) {
      for (const task of this.tasksToEdit) {
        task.auto_approval = value;
      }
    }
  }
};
</script>

<style scoped>
.activity-form-container {
  border-radius: 2px;
  box-shadow: -0.5px 0.5px 2px rgba(0, 0, 0, 0.5);
  padding-top: 10px;
  padding-bottom: 20px;
  margin-bottom: 5px;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/_SpecialTabs/Activities/ActivityDetails/ActivityDetailsForm.vue